import React, { useCallback, useEffect, useMemo, } from 'react'
import _ from 'lodash'
import { LockedGrid, Grid as UnlockedGrid, Panel, ToolBar, SearchField, Container, FieldSet, Button } from '@sencha/ext-modern'

import { renderColumn, getModel, handleItemContextMenu } from 'tyan/public/components/column'
import { reconfigButton } from 'tyan/public/utility'
import { getMixedProps } from 'tyan/public/components/Form/Block/blockPublic'

import { useInstData, useDelayTimer, useEventHandler } from 'tyan/public/hooks'

import { setGridRowNumbersColumnWidth, getColumnInfo, preventCtrlR, innerSearchFn, doInnerSearch, hasMultiLines, hasEditable } from './gridPublic'

const TyanGrid = props => {
	const {
		list: listProps, that, gridID, onSelect,
		onSearch: outerSearchFn, searchStamp,
		innerSearch, // 内部搜索，有内部搜索时，使用内部通用函数进行前台搜索，过滤
		searchMode = 1/* 1:只有搜索框；2:只有列过滤；3：二者皆有（无列过滤时如果在列项上有配置，则列项上有过滤）*/,
		columns, sort,
		multiSelect = false, selectable/*  = multiSelect */, lockable = false,
		contextmenu, onDoubleTap, onDoubleTap2, buttons, hideHeaders = undefined,
		rowNumbers = false, columnLines = false, rowLines = true, border = true, minRows = 2, maxRows = Number.MAX_VALUE, height,
		autoScroll, style,
		rowHeight = 41,
		OuterComp = null, renderInner = null, refreshStamp,
		summary,		// 是否统计
		handleEdit,
		handleBatchEdit,
		groupField
	} = props

	const onSearch = innerSearch ? innerSearchFn : outerSearchFn

	const { me, setState } = useInstData(props, useMemo(() => ({
		state: {},
		oldList: null,
		oldColumns: columns,
		gridKey: Tyan.getUuid(),
		store: null,
		searchStamp: undefined,
		tmrSearch: null,
		searchString: '',
		lastSearchString: '',
		filterChoices: {},  // 给column 保存当前filter choice使用{'编号':'等于'}
		filter: {},
		lastFilter: {}
	}), []))

	// console.log('render:', value, (that.state.changedValuesBasic||{})['_checkgrid-added'])
	if (me.searchStamp != searchStamp) {
		me.searchStamp = searchStamp
		me.tmrSearch = null
		me.searchString = ''
		me.lastSearchString = ''
		me.filter = {}
		me.lastFilter = {}
	}

	const { doDelay: delaySearch } = useDelayTimer()
	const { newDelay: delayAutoScroll, stopDelay: stopDelayAutoScroll } = useDelayTimer()

	const handleSelect = useEventHandler((grid) => onSelect && onSelect(grid.getSelections()))
	const handleStoreChange = useEventHandler((grid, store) => grid && store && handleSelect(grid))
	const handleColumnSort = useEventHandler((grid, column, direction) => me.sort = [{ property: column.config.dataIndex, direction }])
	const doSearch = useEventHandler(() => (
		me.lastSearchString = me.searchString,
		me.lastFilter = _.cloneDeep(me.filter),
		onSearch && onSearch(me.searchString, me.filter, setState))
	)
	const trySearch = useEventHandler(() => delaySearch(doSearch, !(me.searchString == me.lastSearchString && _.isEqual(me.filter, me.lastFilter))))
	const handleSearch = useEventHandler((obj, newValue) => (me.searchString = newValue, trySearch()))
	const handlePainted = useCallback(grid => {
		if (!(grid.isVisible() && grid.dataItems.length))
			return

		grid.getColumns().forEach(col => col.measureWidth() /* col.getComputedWidth() !== col.el.measure('w') && col.setComputedWidth(col.el.measure('w')) */)
	}, [])

	// const handleRefresh = useEventHandler(grid => {
	// 	const doRefresh = () => {
	// 		if (!grid || grid.destroyed) return
	// 		const { position: { x = 0 } = {} } = grid.getScrollable() || {}
	// 		const columns = grid.getColumns()
	// 		const column = columns[columns.length - 1]
	// 		column && (column.isHidden() ? (column.show(), column.hide()) : (column.hide(), column.show()))
	// 		x && grid.getScrollable().scrollTo(x)
	// 	}
	// 	columns && !columns.find(o => o.edit) && setTimeout(doRefresh)
	// })

	// autoScroll: {
	// 	active:
	// 	interval: 5000,				/* 间隔时间，默认5000毫秒 */
	//  duration:500, 				/* 滚动动画时间 */
	// 	onEnded: showNext				/* 每遍呈现之后回调 */
	// }
	const doAutoScroll = useCallback(() => {
		const grid = me.grid
		const autoScroll = me.autoScroll

		if (me.unmounted || !grid || !autoScroll)
			return

		const { interval = 5000, duration = 500, active = true, onEnded } = autoScroll
		const scroller = grid.getScrollable()
		const y = scroller.getPosition().y
		const yMax = scroller.getMaxPosition().y
		const yClient = scroller.getClientSize().y

		if (y == yMax) {
			onEnded && onEnded()
			scroller.scrollTo(null, 0, { duration })
			active && delayAutoScroll(doAutoScroll, interval)
		} else {
			const step = (Math.floor(yClient / 41) - 1) * 41
			scroller.scrollBy(null, step, { duration })
			active && delayAutoScroll(doAutoScroll, interval)
		}
	}, [])

	useEffect(() => {
		if (_.isEqual(autoScroll, me.autoScroll))
			return
		me.autoScroll = autoScroll

		true && (autoScroll && autoScroll.active) ? delayAutoScroll(doAutoScroll, autoScroll.interval || 3000) : stopDelayAutoScroll()
	}, [autoScroll])

	useEffect(() => {
		refreshStamp !== undefined && me.grid && me.grid.refresh()
	}, [refreshStamp])

	// 计算行号宽度
	useEffect(() => {
		setGridRowNumbersColumnWidth(me.grid)
	})

	/**
	 * || !_.isEqual(columns, me.oldColumns) ???忘记为什么要加这个判断，但如果columns中有事件函数并每次为新函数会重复事件而出错
	 * 20220426
	 * 当Columns顺序发生变化时，如果不重新生成store会出现数据列位置不随column位置改变的情况，
	 * 修改为不比较函数的判断 
	 * */
	const columnsChanged = me.oldColumns !== columns
		&& !_.isEqualWith(columns, me.oldColumns, (a, b) => typeof a === 'function' && typeof b === 'function' ? true : undefined)
	me.gridKey = columnsChanged ? Tyan.getUuid() : me.gridKey

	// 计算高度，如果未设置maxRows（Number.MAX_VALUE)，则flex=1；
	const gridDefaults = {
		// leftRegionDefaults: {
		// 	locked: true, grid: {}
		// },
		listeners: {
			columnSort: handleColumnSort,
			select: handleSelect,
			deselect: handleSelect,
			storeChange: handleStoreChange,
			// refresh: handleRefresh,
			paint: handlePainted
		}
	}
	const editable = !!(handleEdit && hasEditable(columns))

	editable && (gridDefaults.platformConfig = {
		desktop: {
			plugins: {
				gridcellediting: true
			}
		},
		'!desktop': {
			plugins: {
				grideditable: true
			}
		}
	})

	const handleInnerEdit = useEventHandler((store, record, operation) => {
		if (operation !== 'edit') return

		const { data: { id } = data, data, modified } = record || {}
		const itemName = Object.keys(modified)[0]
		handleEdit && handleEdit({ id, name: itemName, newValue: data[itemName], data, record })
	})

	// 如果前台搜索，过滤list数据
	const list = innerSearch ? doInnerSearch(listProps, me) : listProps
	if (!_.isEqual(list, me.oldList) || columnsChanged) {
		me.oldList = _.cloneDeep(list)
		me.oldColumns = columns

		const store = me.store = new Ext.data.Store({
			model: getModel({ columns, handleEdit }), data: _.cloneDeep(list),
			...editable ? { listeners: { update: handleInnerEdit } } : {},
			groupField
		})

		// console.log('store:', store)
		!me.sort && sort && (me.sort = Array.isArray(sort) ? sort : [sort])
		const sortConfig = me.sort || []
		sortConfig.length && store.sort(sortConfig)
	}

	hasMultiLines(columns) && (gridDefaults.variableHeights = true)
	gridDefaults.itemConfig = {
		viewModel: true,
		...gridDefaults.variableHeights ? {} : { height: rowHeight + 1 }
	}
	gridDefaults.minHeight = (hideHeaders ? 2 : 45) + rowHeight * minRows - 2
	height
		? gridDefaults.flex = 1
		: gridDefaults.maxHeight = (hideHeaders ? 2 : 45) + rowHeight * maxRows - 2

	style && (gridDefaults.style = style)
	// console.log('store:', me.store)
	!rowLines && (gridDefaults.rowLines = false)
	!border && (gridDefaults.border = false)
	columnLines && (gridDefaults.columnLines = true)
	hideHeaders && (gridDefaults.hideHeaders = true)
	summary && (
		gridDefaults.plugins = { summaryrow: true },
		gridDefaults.groupFooter = { xtype: 'gridsummaryrow' }
	)

	const Grid = useMemo(() => lockable ? LockedGrid : UnlockedGrid, lockable)

	gridDefaults.selectable = selectable === false ? false : multiSelect
		// {drag: true, extensible: "both", rows: true, cells: false, columns: false, }
		? { drag: true, /* mode: 'multi',  */rows: true, cells: false, columns: false, deselectable: true }
		: /* { mode: 'single' } */{}

	gridDefaults.listeners.childContextMenu = gridDefaults.listeners.childLongPress
		= useEventHandler((grid, { record, event }) => contextmenu && handleItemContextMenu(contextmenu, grid, record, event, that))
	gridDefaults.listeners.childDoubleTap = useEventHandler((grid, location) => {
		if (location.recordIndex < 0)
			return

		const { record: { data } = {}, record } = location || {}

		onDoubleTap2
			? onDoubleTap2({ data, record, grid, that, location, column: getColumnInfo(grid, location) })
			: onDoubleTap && onDoubleTap(data, record, grid, that, location)
	})

	const optGridProps = lockable
		? { gridDefaults, leftRegionDefaults: { locked: true, grid: {} } }
		: gridDefaults
	const leftProps = lockable ? optGridProps.leftRegionDefaults.grid : gridDefaults

	selectable !== false && multiSelect && ((leftProps.selectable = leftProps.selectable || {}).checkbox = true)

	rowNumbers && (leftProps.rowNumbers = '33px')	// { width: `${me.store ? (33 + Math.floor(Math.log10(me.store.data.length || 1)) * 7) : 33}px` })

	// console.log('optGridProps:', optGridProps, selectable, multiSelect)
	// !isPhone && multiSelect && (gridDefaults.selectable = { extensible: "both", rows: true, cells: false, columns: false, drag: true })
	// const gridProps = (Grid == 'LockedGrid') ? {
	// 	gridDefaults, ...(multiSelect ? { leftRegionDefaults: { locked: true, grid: { selectable: { checkbox: true } } } } : {})
	// } : Ext.merge(gridDefaults, multiSelect ? { selectable: { checkbox: true } } : {})

	// 如有编辑列项，置所有列不可排序
	const sortableProps = !!columns.find(o => o.edit) ? { sortable: false, menuDisabled: true } : {}
	const ret = <>
		<Grid key={me.gridKey} {...gridID ? { id: `${gridID}_${me.gridKey}` } : {}} ref={ref => ref && (/* window.grid =  */me.grid = ref.cmp)}
			style={{ borderStyle: 'solid', borderWidth: '1px', borderColor: '#e2e2e2' }} {...optGridProps} store={me.store} shadow={false}
			// height={`${(hideHeaders ? 2 : 45) + 41 * iRows}px`}
			width="100%" scrollable
		// itemConfig={itemConfig}
		>
			{renderInner ? renderInner() : null}
			{columns.map(({ search, filter, ...column }, key) => renderColumn({
				filter: onSearch && searchMode & 2 ? filter : undefined,
				...column,
				filterProp: me.filter, filterChoices: me.filterChoices, onFilterChange: trySearch,
				handleEdit,
				handleBatchEdit,
				...sortableProps
			}, key, that))}
		</Grid>
		{
			((onSearch && searchMode & 1) || (buttons && buttons.length > 0)) &&
			<ToolBar docked="bottom" ui="app-transparent-toolbar" shadow={false} margin="0 0 0 0" padding="0 0 0 0" layout={{ type: 'hbox', align: 'center' }}>
				{onSearch && (searchMode & 1) !== 0 && (
					<>
						<SearchField align="right" ui="solo ks-search-right-trigger" width="280"
							value={me.searchString}
							placeholder={`搜索 ...`}
							onChange={handleSearch}
							margin="0 10 0 0"
							keyMap={{ 'Ctrl+R': { handler: preventCtrlR } }}
						/>
						<Panel padding="9 20 9 20" >
							{/* <div>{`共 ${list.length} 条记录，已选择 ${addedIds.length} 条记录`}</div> */}
							<div>{`共 ${list.length} 条记录`}</div>
						</Panel>
					</>
				)}
				{
					buttons && buttons.length > 0 && <Container layout={{ type: 'hbox', pack: 'end' }} margin="10px 0px 0px 0px" flex={1}/*  width="100%" */>
						{buttons.map((o1, i) => {
							const o = reconfigButton(o1)
							const { props: btnProps } = getMixedProps('_button', [o])
							o.disabled && typeof o.disabled === 'function' && (btnProps.disabled = o.disabled())
							o.icon != undefined && (typeof o.icon === 'function' ? (btnProps.iconCls = `x-fa fa-${o.icon()}`) : btnProps.iconCls = `x-fa fa-${o.icon}`)
							btnProps.margin = '0px 0px 0px 20px'

							return o.cdt && !o.cdt() ? null : <Button key={i} text={o._button} {...btnProps} />
						})}
					</Container>
				}
			</ToolBar>
		}
	</>

	return OuterComp ? <OuterComp>{ret}</OuterComp> : ret
}

export default TyanGrid
